/**
 * Data hook for GET /patients/{patient_id}/story-map.
 * Errors are loud: surfaced via `error` state and console.error, never swallowed.
 */
import { useEffect, useRef, useState } from "react";
import { API_BASE } from "../../config";
import { useAuth } from "../../auth/AuthProvider";
import type { StoryMapResponse } from "./types";

export function useStoryMap(patientId: string) {
  const { ensureToken } = useAuth();
  const tokenRef = useRef(ensureToken);
  useEffect(() => {
    tokenRef.current = ensureToken;
  }, [ensureToken]);

  const [data, setData] = useState<StoryMapResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!patientId?.trim()) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await tokenRef.current();
        if (!token) throw new Error("Not authenticated");
        const resp = await fetch(
          `${API_BASE}/patients/${encodeURIComponent(patientId)}/story-map`,
          { headers: { Authorization: `Bearer ${token}` }, signal: ac.signal },
        );
        const text = await resp.text();
        let json: unknown = {};
        try {
          json = text ? JSON.parse(text) : {};
        } catch {
          console.error("[useStoryMap] JSON parse failed:", { status: resp.status, text });
          throw new Error(`HTTP ${resp.status} (non-JSON response)`);
        }
        if (!resp.ok) {
          throw new Error((json as { error?: string })?.error || `HTTP ${resp.status}`);
        }
        const status = (json as { status?: string })?.status;
        if (status !== "ok" && status !== "needs_more_sessions") {
          throw new Error("unexpected story-map response shape");
        }
        if (ac.signal.aborted) return;
        setData(json as StoryMapResponse);
      } catch (e: any) {
        if (ac.signal.aborted) return;
        console.error("[useStoryMap] fetch failed:", e);
        setError(e?.message || "fetch failed");
        setData(null);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [patientId]);

  return { data, loading, error };
}
